/* eslint-disable react-refresh/only-export-components --
 * Provider component + useContext hook + a small label helper live in
 * this one file by design — the standard React Context pattern. The
 * cost is that Vite's HMR fully reloads on edits to this file rather
 * than preserving state. Acceptable trade-off for a single source of
 * truth on framework data.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  useApi,
  type FrameworkAdditionRow,
  type FrameworkOverlayResponse,
  type FrameworkOverrideRow,
} from "../api/client";
import {
  DOMAINS as DEFAULT_DOMAINS,
  SUBDOMAINS as DEFAULT_SUBDOMAINS,
} from "./data";
import type { Domain, DomainId, PouId, Subdomain, SubdomainId } from "./types";

/**
 * Framework runtime layer.
 *
 * Merges the code-default DOMAINS / SUBDOMAINS (src/framework/data.ts)
 * with the admin overlay from /api/v1/admin/framework:
 *
 *   - framework_addition  → extra domains/subdomains appended to the list
 *   - framework_override  → renames on defaults + soft-delete via `hidden`
 *
 * Components read the merged framework via the `useFramework()` hook.
 * The provider is mounted alongside `StateSettingsProvider` near the top
 * of the authenticated app, so the GET fires once per session.
 *
 * Pages that need to know whether an id is a code-default (eg. the editor's
 * "delete" button is only available on additions) compare against
 * `DEFAULT_DOMAIN_IDS` / `DEFAULT_SUBDOMAIN_IDS`, also exported here.
 *
 * Conservative behaviour for hidden defaults: the merged framework EXCLUDES
 * hidden items from `domains` / `subdomains`, but the raw overrides are
 * also exposed so consumers (eg. WhanauProfile rendering historical data
 * against a retired subdomain) can detect the case and render a 'Retired'
 * chip rather than silently dropping the row.
 */

export const DEFAULT_DOMAIN_IDS: ReadonlySet<DomainId> = new Set(
  DEFAULT_DOMAINS.map((d) => d.id),
);
export const DEFAULT_SUBDOMAIN_IDS: ReadonlySet<SubdomainId> = new Set(
  DEFAULT_SUBDOMAINS.map((s) => s.id),
);

export type MergedDomain = Domain & {
  /** 'default' = from data.ts; 'custom' = from framework_addition table. */
  readonly source: "default" | "custom";
  /** Te reo Māori name, when explicitly set by an addition or rename override. */
  readonly nameMi: string | null;
  /** True if a rename override exists — consumers should prefer nameEn/nameMi over the i18n key. */
  readonly hasNameOverride: boolean;
};

export type MergedSubdomain = Subdomain & {
  readonly source: "default" | "custom";
  readonly nameMi: string | null;
  readonly hasNameOverride: boolean;
};

type FrameworkContextValue = {
  /** Domains visible in the wizard, framework page, etc. Hidden ones excluded. */
  readonly domains: readonly MergedDomain[];
  /** Subdomains visible in the wizard, etc. Hidden ones excluded. */
  readonly subdomains: readonly MergedSubdomain[];
  /** Find a domain by id (returns hidden ones too — for retired-data rendering). */
  readonly findDomain: (id: string) => MergedDomain | undefined;
  /** Find a subdomain by id (returns hidden ones too). */
  readonly findSubdomain: (id: string) => MergedSubdomain | undefined;
  /** All subdomains for a given domain, in display order. Hidden excluded. */
  readonly subdomainsForDomain: (
    domainId: string,
  ) => readonly MergedSubdomain[];
  /** True if the given id was hidden by an admin override. */
  readonly isHidden: (kind: "domain" | "subdomain", id: string) => boolean;
  /** Raw overlay for the editor — additions + overrides as returned by the API. */
  readonly overlay: FrameworkOverlayResponse | null;
  /** Loading / error state. */
  readonly loadState: "loading" | "ok" | "error";
  /** Manually refresh the overlay (called by the editor after a save). */
  readonly refresh: () => Promise<void>;
};

// Default value — used when no provider is mounted (eg. tests). Returns the
// code defaults with no overlay applied.
const defaultDomains: MergedDomain[] = DEFAULT_DOMAINS.map((d) => ({
  ...d,
  source: "default" as const,
  nameMi: null,
  hasNameOverride: false,
}));
const defaultSubdomains: MergedSubdomain[] = DEFAULT_SUBDOMAINS.map((s) => ({
  ...s,
  source: "default" as const,
  nameMi: null,
  hasNameOverride: false,
}));

const FrameworkContext = createContext<FrameworkContextValue>({
  domains: defaultDomains,
  subdomains: defaultSubdomains,
  findDomain: (id) => defaultDomains.find((d) => d.id === id),
  findSubdomain: (id) => defaultSubdomains.find((s) => s.id === id),
  subdomainsForDomain: (domainId) =>
    defaultSubdomains.filter((s) => s.domainId === domainId),
  isHidden: () => false,
  overlay: null,
  loadState: "ok",
  refresh: async () => undefined,
});

export function FrameworkProvider({ children }: { children: ReactNode }) {
  const callApi = useApi();
  const [overlay, setOverlay] = useState<FrameworkOverlayResponse | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ok" | "error">(
    "loading",
  );

  const fetchOverlay = useMemo(
    () => async (): Promise<void> => {
      try {
        const data =
          await callApi<FrameworkOverlayResponse>("/admin/framework");
        setOverlay(data);
        setLoadState("ok");
      } catch {
        // Network / auth failure: fall back to code defaults (overlay = null).
        // We deliberately don't surface this — the framework still works.
        setOverlay(null);
        setLoadState("error");
      }
    },
    [callApi],
  );

  useEffect(() => {
    void fetchOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<FrameworkContextValue>(() => {
    if (overlay === null) {
      return {
        domains: defaultDomains,
        subdomains: defaultSubdomains,
        findDomain: (id) => defaultDomains.find((d) => d.id === id),
        findSubdomain: (id) => defaultSubdomains.find((s) => s.id === id),
        subdomainsForDomain: (domainId) =>
          defaultSubdomains.filter((s) => s.domainId === domainId),
        isHidden: () => false,
        overlay: null,
        loadState,
        refresh: fetchOverlay,
      };
    }
    return buildMergedFramework(overlay, loadState, fetchOverlay);
  }, [overlay, loadState, fetchOverlay]);

  return (
    <FrameworkContext.Provider value={value}>
      {children}
    </FrameworkContext.Provider>
  );
}

export function useFramework(): FrameworkContextValue {
  return useContext(FrameworkContext);
}

/**
 * Returns the label for a domain or subdomain in the current language.
 *
 *   - If the item is a custom addition, use its explicitly-recorded nameEn / nameMi.
 *   - If the item has a rename override, use the merged nameEn / nameMi
 *     (preferring nameMi when present in te reo mode).
 *   - Otherwise look up the i18n catalogue (eg. framework.subdomain.parenting).
 *
 * Always returns a non-empty string. If everything is missing it falls back
 * to the id.
 */
export function frameworkLabel(
  item: MergedDomain | MergedSubdomain,
  kind: "domain" | "subdomain",
  language: string,
  t: (key: string) => string,
): string {
  const isMi = language === "mi";
  if (item.source === "custom" || item.hasNameOverride) {
    if (isMi && item.nameMi) return item.nameMi;
    if (item.nameEn) return item.nameEn;
    return item.id;
  }
  // Default item with no rename override — use the i18n catalogue.
  const key = `framework.${kind}.${item.id}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  return item.nameEn || item.id;
}

// ----------------------------- merge logic ---------------------------------

function buildMergedFramework(
  overlay: FrameworkOverlayResponse,
  loadState: "loading" | "ok" | "error",
  refresh: () => Promise<void>,
): FrameworkContextValue {
  const domainOverrides = new Map<string, FrameworkOverrideRow>();
  const subdomainOverrides = new Map<string, FrameworkOverrideRow>();
  for (const o of overlay.overrides) {
    if (o.kind === "domain") domainOverrides.set(o.id, o);
    else subdomainOverrides.set(o.id, o);
  }

  const domainAdditions: FrameworkAdditionRow[] = [];
  const subdomainAdditions: FrameworkAdditionRow[] = [];
  for (const a of overlay.additions) {
    if (a.kind === "domain") domainAdditions.push(a);
    else subdomainAdditions.push(a);
  }

  // -- Domains --
  const buildDefaultDomain = (
    d: (typeof DEFAULT_DOMAINS)[number],
    o: FrameworkOverrideRow | undefined,
  ): MergedDomain => ({
    id: d.id,
    nameEn: o?.name_en ?? d.nameEn,
    nameMi: o?.name_mi ?? null,
    source: "default",
    hasNameOverride: !!(o && (o.name_en != null || o.name_mi != null)),
  });
  const buildCustomDomain = (a: FrameworkAdditionRow): MergedDomain => ({
    id: a.id,
    nameEn: a.name_en,
    nameMi: a.name_mi,
    source: "custom",
    hasNameOverride: true,
  });

  const allDomains: MergedDomain[] = [];
  for (const d of DEFAULT_DOMAINS) {
    const o = domainOverrides.get(d.id);
    if (o?.hidden) continue;
    allDomains.push(buildDefaultDomain(d, o));
  }
  domainAdditions.sort(
    (a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id),
  );
  for (const a of domainAdditions) allDomains.push(buildCustomDomain(a));

  // -- Subdomains --
  const buildDefaultSubdomain = (
    s: (typeof DEFAULT_SUBDOMAINS)[number],
    o: FrameworkOverrideRow | undefined,
  ): MergedSubdomain => ({
    id: s.id,
    domainId: s.domainId,
    pouId: s.pouId,
    nameEn: o?.name_en ?? s.nameEn,
    nameMi: o?.name_mi ?? null,
    // Admin can override the seeded ToC string via the framework editor.
    // Null override = use the seed value from src/framework/data.ts.
    toCOutcome: o?.toc_outcome ?? s.toCOutcome,
    source: "default",
    hasNameOverride: !!(o && (o.name_en != null || o.name_mi != null)),
  });
  const buildCustomSubdomain = (
    a: FrameworkAdditionRow,
  ): MergedSubdomain | null => {
    if (a.domain_id == null || a.pou_id == null) return null;
    return {
      id: a.id,
      domainId: a.domain_id,
      pouId: a.pou_id as PouId,
      nameEn: a.name_en,
      nameMi: a.name_mi,
      toCOutcome: a.toc_outcome ?? "",
      source: "custom",
      hasNameOverride: true,
    };
  };

  const allSubdomains: MergedSubdomain[] = [];
  for (const s of DEFAULT_SUBDOMAINS) {
    const o = subdomainOverrides.get(s.id);
    if (o?.hidden) continue;
    allSubdomains.push(buildDefaultSubdomain(s, o));
  }
  subdomainAdditions.sort(
    (a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id),
  );
  for (const a of subdomainAdditions) {
    const m = buildCustomSubdomain(a);
    if (m !== null) allSubdomains.push(m);
  }

  // For find* helpers we want to be able to look up *hidden* items too,
  // so build full maps separately (no `hidden` skip).
  const allDomainsIncHidden = new Map<string, MergedDomain>();
  for (const d of DEFAULT_DOMAINS) {
    allDomainsIncHidden.set(
      d.id,
      buildDefaultDomain(d, domainOverrides.get(d.id)),
    );
  }
  for (const a of domainAdditions)
    allDomainsIncHidden.set(a.id, buildCustomDomain(a));

  const allSubdomainsIncHidden = new Map<string, MergedSubdomain>();
  for (const s of DEFAULT_SUBDOMAINS) {
    allSubdomainsIncHidden.set(
      s.id,
      buildDefaultSubdomain(s, subdomainOverrides.get(s.id)),
    );
  }
  for (const a of subdomainAdditions) {
    const m = buildCustomSubdomain(a);
    if (m !== null) allSubdomainsIncHidden.set(a.id, m);
  }

  return {
    domains: allDomains,
    subdomains: allSubdomains,
    findDomain: (id) => allDomainsIncHidden.get(id),
    findSubdomain: (id) => allSubdomainsIncHidden.get(id),
    subdomainsForDomain: (domainId) =>
      allSubdomains.filter((s) => s.domainId === domainId),
    isHidden: (kind, id) =>
      kind === "domain"
        ? domainOverrides.get(id)?.hidden === true
        : subdomainOverrides.get(id)?.hidden === true,
    overlay,
    loadState,
    refresh,
  };
}
